import { ChangeEvent, HTMLInputTypeAttribute } from 'react';

const correctCommonAcronyms = (value: string) => {
  let correction = value;
  const corrections = [
    { find: 'Wi Fi ', replace: 'WiFi ' },
    { find: 'Ip ', replace: 'IP ' },
    { find: 'Ip', replace: 'IP' },
    { find: 'Dhcp ', replace: 'DHCP ' },
    { find: 'Ap ', replace: 'AP ' },
    { find: 'Channel24G', replace: 'Channel 24G' },
    { find: 'Channel5G', replace: 'Channel 5G' },
    { find: 'Latency24G', replace: 'Latency 24G' },
    { find: 'Latency5G', replace: 'Latency 5G' },
    { find: 'Ss Id', replace: 'SSID' },
    { find: 'Bss Id', replace: 'BSSID' },
  ];
  const search = corrections.map((k) => k.find);

  search.forEach((v) => {
    if (value.includes(v)) {
      const correct = corrections.find((c) => c.find === v);
      correct
        ? (correction = value.replace(correct.find, correct.replace))
        : null;
    }
  });

  return correction;
};

export const buildQueryString = (params: object) => {
  let queryParams = '';

  Object.entries(params).forEach((param, index) => {
    const [key, value] = param;

    if (index === 0 && value) {
      queryParams += `?${key}=${value}`;
    } else if (value) {
      queryParams += `&${key}=${value}`;
    }
  });

  return queryParams;
};

export const camelToTitleCase = (value: string) => {
  const addSpace = value.replace(/([A-Z][a-z])/g, ' $1');
  return correctCommonAcronyms(
    addSpace.charAt(0).toUpperCase() + addSpace.slice(1)
  );
};

export const dataTypeFormatter = (data: ApiDataDetail, allCounters: any) => {
  const render = eval(
    ((data as ApiDataDetailLower).render ??
      (data as ApiDataDetailUpper).Render) ||
      ''
  );
  const formatter = eval(
    ((data as ApiDataDetailLower).formatter ??
      (data as ApiDataDetailUpper).Formatter) ||
      ''
  );
  let value = getPrimitive(
    (data as ApiDataDetailLower).value ?? (data as ApiDataDetailUpper).Value,
    (data as ApiDataDetailLower).dataType ??
      (data as ApiDataDetailUpper).DataType
  );

  if (value !== null) {
    if (typeof formatter === 'function') {
      value = formatter(value);
    }
    if (typeof render === 'function') {
      value = render(value, allCounters);
    }

    return value !== '' ? value : 'N/A';
  }

  return (
    (data as ApiDataDetailLower).value ?? (data as ApiDataDetailUpper).Value
  );
};

export const formatNumberToCurrency = (
  number: number,
  locale?: string,
  options?: Intl.NumberFormatOptions
) => {
  return new Intl.NumberFormat(
    locale ?? 'en-US',
    options ?? { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }
  ).format(number);
};

const getPrimitive = (raw: string, dataType: string) => {
  let primitive = null;
  switch (dataType.toLowerCase()) {
    case 'integer':
    case 'double':
    case 'int64':
    case 'int32':
    case 'int':
      const i = parseInt(raw);
      if (!isNaN(i) && isFinite(i)) primitive = i;
      break;
    case 'date':
    case 'datetime':
      if (raw !== null) {
        const d = new Date(raw);
        if (d instanceof Date) primitive = d.toLocaleString();
      }
      break;
    case 'timeticks':
      const curTime = new Date().getTime();
      const x = parseInt(raw);
      if (!isNaN(x) && isFinite(x)) primitive = new Date(curTime - x * 10);
      break;
    default:
      primitive = raw;
  }
  return primitive;
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getEventValueAsType = (event: ChangeEvent<any>) => {
  if (event.target.type === 'number') return event.target.valueAsNumber;
  else if ((event.target.type as HTMLInputTypeAttribute) === 'date')
    return event.target.valueAsDate;
  else return event.target.value;
};

export const getTimeDiff = (startTime: string, endTime?: string | null) => {
  let diff: any =
      (endTime ? new Date(endTime).getTime() : Date.now()) -
      new Date(startTime).getTime(),
    seconds = Math.floor((diff / 1000) % 60),
    minutes = Math.floor((diff / (1000 * 60)) % 60),
    hours = Math.floor((diff / (1000 * 60 * 60)) % 24),
    days = Math.floor(diff / (24 * 60 * 60 * 1000));

  const d = days > 0 ? `${days}d ` : '';
  const h = hours > 0 ? `${hours}h ` : '';
  const m = minutes > 0 ? `${minutes}m ` : '';
  const s = seconds > 0 ? `${seconds}s` : '';

  if (days + hours + minutes === 0) return '< 1m';
  return d + h + m;
};

export const decodeHtmlEntityString = (text: string) => {
  const entities: Record<string, string> = {
    amp: '&',
    apos: "'",
    lt: '<',
    gt: '>',
    quot: '"',
    nbsp: '\xa0',
  };
  const entityPattern = /&([a-z]+);/gi;
  return text.replace(entityPattern, function (match, entity: string) {
    entity = entity.toLowerCase();
    if (entities.hasOwnProperty(entity)) {
      return entities[entity];
    }
    return match;
  });
};

export const formatDateTimeLocalInput = (date: Date, showSeconds?: boolean) => {
  const localISOString = new Date(date.toString().split('GMT')[0] + ' UTC')
    .toISOString()
    .split('.')[0];

  if (showSeconds) return localISOString;
  return localISOString.substring(0, localISOString.length - 3);
};

export const formatDateInput = (date: Date) => {
  return [
    date.toLocaleString('en-US', { year: 'numeric' }),
    date.toLocaleString('en-US', { month: '2-digit' }),
    date.toLocaleString('en-US', { day: '2-digit' }),
  ].join('-');
};
